// @ts-nocheck
import React, { useEffect, useRef, useCallback, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { Box, Stack, Button, TextField, Typography, Icons, DateTimeStandard } from "@curves/ui-base";
import { ProductInfo, GradientColorHeader, ReadTextField } from "@curves/ui-shared";
import { EmployeeSelect } from "@icoach/components";
import GiftSoloCreateOptionDialog from "@icoach/gift/components/GiftSoloCreateOptionDialog";
import { callRef, isFunction, refIsRequiredError } from "@util/utils";
import { getNowTime, parseDate } from "@util/moment";
import useGetEmployeeOptionState from "@util/hook/useGetEmployeeOptionState";
import useClubInventoryApi from "@apis/useClubInventoryApi";
import useClubStockOutApi from "@apis/useClubStockOutApi";
import useGiftItemApi from "@apis/useGiftItemApi";
import { useSnackbar } from "notistack";

/**
 * 贈品領用 Dialog（店鋪庫存明細開單）。
 *
 * 數量語意：
 * - claimQuantity／領用數量：從本筆庫存（productID + variantID）扣減的單位數（例：1 箱）。
 * - quantity／贈品數量：領用換算後的贈品單位數，會累加進 GiftItem.Quantity（例：1 箱 → 24 包）。
 */
const GiftClaimDialog = ({ open, onClose, sourceData = null, refresh }) => {
	// 表單欄位 ref
	const creatorRef = useRef(null);
	const giftTypeRef = useRef(null);
	/** 領用數量：扣來源庫存 */
	const claimQtyRef = useRef(null);
	/** 贈品數量：換算後累加進 GiftItem.Quantity */
	const quantityRef = useRef(null);
	const [detail, setDetail] = useState(null);
	const [enabledGiftItems, setEnabledGiftItems] = useState([]);
	const { enqueueSnackbar } = useSnackbar();
	const { employeeOption } = useGetEmployeeOptionState();

	// API：讀取明細、送出調整；啟用中贈品清單供 Autocomplete 使用
	const { getClubInventoryDetailApi } = useClubInventoryApi();
	const { postClubStockOutGiftConsumptionApi } = useClubStockOutApi();
	const { getGiftItemEnabledApi } = useGiftItemApi();

	const giftAutocompleteOptions = useMemo(
		() => (Array.isArray(enabledGiftItems) ? enabledGiftItems : []),
		[enabledGiftItems],
	);

	const productID = sourceData?.productID != null ? Number(sourceData.productID) : null;
	const variantID = sourceData?.variantID != null ? Number(sourceData.variantID) : 0;

	// 開啟且具備 productID 時取得庫存明細
	useEffect(() => {
		// 若 open / productID / variantID 變更導致 effect 重跑，舊的請求回來時不應覆蓋新狀態
		let isCancelled = false;

		const fetchDetail = async () => {
			if (!open || productID == null) {
				setDetail(null);
				return;
			}
			try {
				const resp = await getClubInventoryDetailApi({ productID, variantID });
				if (isCancelled) return;
				setDetail(resp ?? null);
			} catch (e) {
				if (!isCancelled) setDetail(null);
			}
		};

		fetchDetail();
		return () => {
			isCancelled = true;
		};
	}, [open, productID, variantID, getClubInventoryDetailApi]);

	useEffect(() => {
		let cancelled = false;
		const loadGifts = async () => {
			if (!open) {
				setEnabledGiftItems([]);
				return;
			}
			const resp = await getGiftItemEnabledApi();
			if (!cancelled) {
				setEnabledGiftItems(Array.isArray(resp) ? resp : []);
			}
		};
		loadGifts();
		return () => {
			cancelled = true;
		};
	}, [open, getGiftItemEnabledApi]);

	// 顯示資料：detail 欄位 keyname 已由介面統一，這裡直接取值
	const productInfo = detail
		? {
				sku: detail.sku ?? "",
				name: detail.productName ?? "",
				spec: detail.variantName ?? "",
				imageUrl: detail.pictureSrc ?? "",
			}
		: null;
	const todayDisplayDate = parseDate(new Date(), DateTimeStandard.DisplayDateBySlash);

	// 關閉：交由父層控制 open；本層 detail 會在 open=false 時自動清空
	const handleClose = useCallback(e => isFunction(onClose) && onClose(e), [onClose]);

	// 送出：驗證通過後呼叫贈品領用 API
	const handleSubmit = async () => {
		// 必填驗證
		const requiredRefs = [creatorRef, giftTypeRef, claimQtyRef, quantityRef];
		if (refIsRequiredError(...requiredRefs)) return;

		// 領用數量：扣庫存；驗證為整數且 > 0
		const claimQtyRaw = callRef(claimQtyRef, "getResult") || "";
		const claimQty = Number(claimQtyRaw);
		const isClaimQtyValid = claimQtyRaw !== "" && !Number.isNaN(claimQty) && Number.isInteger(claimQty) && claimQty > 0;
		if (!isClaimQtyValid) {
			enqueueSnackbar("請輸入有效的領用數量（整數且 > 0）", { variant: "warning" });
			return;
		}
		// 贈品數量：換算後累加進 GiftItem.Quantity；驗證為整數且 > 0
		const quantityRaw = callRef(quantityRef, "getResult") || "";
		const quantity = Number(quantityRaw);
		const isQuantityValid = quantityRaw !== "" && !Number.isNaN(quantity) && Number.isInteger(quantity) && quantity > 0;
		if (!isQuantityValid) {
			enqueueSnackbar("請輸入有效的贈品數量（整數且 > 0）", { variant: "warning" });
			return;
		}

		const giftItem = callRef(giftTypeRef, "getResult");
		const payload = {
			productID,
			variantID,
			employeeID: Number(callRef(creatorRef, "getResult") || 0),
			createDate: getNowTime(),
			claimQuantity: claimQty,
			quantity,
			giftItem: giftItem || {
				giftID: 0,
				giftName: "",
				giftPrice: 0,
				giftSpecifications: "",
				giftDescription: "",
			},
		};

		const resp = await postClubStockOutGiftConsumptionApi(payload);
		if (resp) {
			if (isFunction(refresh)) refresh();
			handleClose();
		}
	};

	// 未開啟不渲染，避免 ref 狀態與表單內容殘留在畫面外
	if (!open) return null;

	return (
		<Dialog className="simple-content dialog" PaperProps={{ className: "wx-65rem" }} open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<GradientColorHeader onClose={handleClose}>贈品領用</GradientColorHeader>
			<DialogContent className="p-3">
				{productID == null ? null : !detail ? (
					<Typography color="textSecondary">無法取得庫存明細</Typography>
				) : (
					<Box component="div" className="adjust-stock-qty-form">
						<Grid container spacing={3} alignItems="flex-start">
							{/* 左側：商品資訊 */}
							<Grid item xs={4} className="adjust-stock-qty-form-product">
								<ProductInfo product={productInfo} layout="column" imageSize="auto" />
							</Grid>

							{/* 右側：表單欄位 */}
							<Grid item xs={8} className="adjust-stock-qty-form-fields">
								<Grid container spacing={2}>
									<Grid item xs={6}>
										<ReadTextField label="建單日期">{todayDisplayDate}</ReadTextField>
									</Grid>
									<Grid item xs={6}>
										<EmployeeSelect label="建單人" ref={creatorRef} options={employeeOption} fullWidth required />
									</Grid>
									<Grid item xs={12}>
										<GiftSoloCreateOptionDialog
											ref={giftTypeRef}
											label="贈品名稱"
											options={giftAutocompleteOptions}
											fullWidth
											required
										/>
									</Grid>
									<Grid item xs={12}>
										{/* 領用數量 → 扣一般倉庫存；贈品數量 → 換算後累加進 GiftItem.Quantity */}
										<Stack direction="row" spacing={2} alignItems="flex-start" className="adjust-stock-qty-form-qty-row">
											<TextField
												label="領用數量"
												ref={claimQtyRef}
												className="adjust-stock-qty-form-original-qty"
												defaultValue={detail?.lastInventoryCount ?? 0}
												type="number"
												required
												fullWidth
												inputProps={{ min: 1, step: 1 }}
											/>
											<span className="adjust-stock-qty-form-arrow">
												<Icons.ArrowUp className="adjust-stock-qty-form-arrow-icon" />
											</span>
											<div className="adjust-stock-qty-form-adjusted-qty">
												<TextField
													label="贈品數量"
													ref={quantityRef}
													defaultValue={0}
													type="number"
													required
													fullWidth
													inputProps={{ min: 1, step: 1 }}
												/>
											</div>
										</Stack>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				{detail && (
					<Button variant="contained" onClick={handleSubmit}>
						確認
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

GiftClaimDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	sourceData: PropTypes.shape({
		productID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		variantID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	}),
	refresh: PropTypes.func,
};

GiftClaimDialog.displayName = "GiftClaimDialog";

export default GiftClaimDialog;
